import React, { useRef, useEffect } from "react";

function FilterCheckbox({
  label,
  checked,
  onChange,
}: {
  label:    string;
  checked:  boolean;
  onChange: () => void;
}) {
  return (
    <button
      onClick={onChange}
      className="w-full flex items-center gap-2.5 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
    >
      <span className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 transition-colors ${
        checked ? "bg-gray-800 border-gray-800" : "border-gray-300"
      }`}>
        {checked && (
          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="20 6 9 17 4 12" />
          </svg>
        )}
      </span>
      <span>{label}</span>
    </button>
  );
}

export function FilterDropdown({
  isOpen,
  onToggle,
  onClose,
  hasActiveFilters,
  filterUnassigned,
  filterPickups,
  filterDropoffs,
  filterBlackouts,
  filterChangeRequests,
  onToggleUnassigned,
  onTogglePickups,
  onToggleDropoffs,
  onToggleBlackouts,
  onToggleChangeRequests,
  onClear,
}: {
  isOpen:                   boolean;
  onToggle:                 () => void;
  onClose:                  () => void;
  hasActiveFilters:         boolean;
  filterUnassigned:         boolean;
  filterPickups:            boolean;
  filterDropoffs:           boolean;
  filterBlackouts:          boolean;
  filterChangeRequests:     boolean;
  onToggleUnassigned:       () => void;
  onTogglePickups:          () => void;
  onToggleDropoffs:         () => void;
  onToggleBlackouts:        () => void;
  onToggleChangeRequests:   () => void;
  onClear:                  () => void;
}) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [isOpen, onClose]);

  return (
    <div ref={ref} className="relative">
      <button
        onClick={onToggle}
        className={`p-2 rounded-lg transition-colors relative ${
          isOpen || hasActiveFilters
            ? "bg-gray-800 text-white"
            : "bg-white border border-gray-200 text-gray-500 hover:bg-gray-50"
        }`}
        title="Filters"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
        </svg>
        {hasActiveFilters && (
          <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-blue-500 rounded-full border-2 border-white" />
        )}
      </button>

      {isOpen && (
        <div className="absolute right-0 top-10 z-50 w-52 bg-white rounded-lg shadow-lg border border-gray-200 py-2">
          <p className="px-3 pb-1.5 text-xs font-medium text-gray-400 uppercase tracking-wide">Filters</p>

          <FilterCheckbox label="Unassigned"            checked={filterUnassigned}     onChange={onToggleUnassigned} />
          <FilterCheckbox label="Pickups"                checked={filterPickups}        onChange={onTogglePickups} />
          <FilterCheckbox label="Drop offs"              checked={filterDropoffs}       onChange={onToggleDropoffs} />
          <FilterCheckbox label="Hide blackout days"     checked={filterBlackouts}      onChange={onToggleBlackouts} />
          <FilterCheckbox label="Open change requests"   checked={filterChangeRequests} onChange={onToggleChangeRequests} />

          {hasActiveFilters && (
            <>
              <div className="border-t border-gray-100 my-1.5" />
              <button
                onClick={onClear}
                className="w-full text-left px-3 py-1.5 text-xs text-gray-400 hover:text-gray-600 transition-colors"
              >
                Clear all filters
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}
